import { defineStore } from "pinia"
import { apiClient } from "@/services/api"
import { useSettingsStore } from "@/store/settings"

function arrayBufferToBase64(buffer) {
  let binary = ""
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

// Stores the in-flight promise outside of Pinia state to avoid serialization issues
let inflight = null

/**
 * Company logo of the current tenant. Shown in the sidebar in place of the tenant initials.
 * There is exactly one logo per tenant, so - unlike the user avatar store - this caches a single image.
 */
export const useTenantLogoStore = defineStore("tenantLogo", {
  state: () => ({
    // data-URL of the logo (has logo) | null (no logo / fetch failed); undefined means not fetched yet
    logo: undefined,
  }),
  getters: {
    hasLogo: state => !!state.logo,
    // True once the request has settled, so a consumer can avoid flashing the initials first.
    isLoaded: state => state.logo !== undefined,
  },
  actions: {
    /**
     * Returns the cached logo, or fetches it. Concurrent calls share a single in-flight request.
     * The backend answers 404 when the tenant has no logo, which is cached as null so the initials
     * fallback does not re-request on every render.
     * @param force skip both caches and re-request; used after an upload changed the logo.
     */
    fetchLogo({ force = false } = {}) {
      if (!force && this.logo !== undefined) return Promise.resolve(this.logo)
      if (inflight) return inflight

      // Settings are loaded before the app mounts (main.js), so a tenant that is known to have no
      // logo can skip the request altogether instead of provoking a 404 on every page load.
      if (!force && useSettingsStore().settings?.hasLogo === false) {
        this.logo = null
        return Promise.resolve(null)
      }

      inflight = apiClient
        .get("/settings/logo", {
          headers: { tenant: true, authorization: true },
          responseType: "arraybuffer",
        })
        .then(res => {
          // A logo-less tenant answers 404, but guard against an empty 200 body as well.
          if (!res.data || res.data.byteLength === 0) {
            this.logo = null
            return null
          }
          const contentType = res.headers?.["content-type"] || "image/png"
          this.logo = `data:${contentType};base64,${arrayBufferToBase64(res.data)}`
          return this.logo
        })
        .catch(() => {
          this.logo = null
          return null
        })
        .finally(() => {
          inflight = null
        })

      return inflight
    },

    /** Drops the cache so the next fetchLogo re-requests. Call on logout - the next tenant has its own logo. */
    reset() {
      this.logo = undefined
      inflight = null
    },

    /** Drops the cache and re-fetches. Call after a successful upload. */
    reload() {
      this.reset()
      return this.fetchLogo({ force: true })
    },

    /** Marks the tenant as having no logo without re-requesting. Call after a successful delete. */
    clear() {
      this.logo = null
      inflight = null
    },
  },
})
